import { Component, h, Prop, Element, State, Event, EventEmitter, Watch, Method, AttachInternals } from '@stencil/core';

@Component({
  tag: 'spw-checkbox',
  styleUrl: 'spw-checkbox.scss',
  shadow: true,
  formAssociated: true,
})
export class SpwCheckbox {
  private inputElement: HTMLInputElement;

  @Element() el: HTMLSpwCheckboxElement;

  /** Détermine si la case à cocher est désactivée, rendant l'élément inutilisable */
  @Prop() disabled: boolean = false;
  /** Propriété mutable qui reflète si la case à cocher est cochée ou non */
  @Prop({ mutable: true, reflect: true }) checked: boolean = false;
  /** Indique s'il y a une erreur associée à la case à cocher, pour un affichage visuel */
  @Prop() error: boolean = false;
  /** Icône à afficher lorsque la case à cocher est cochée */
  @Prop() icon: string = 'fa-check';
  /** Nom de l'élément utilisé dans les formulaires */
  @Prop() name: string;

  @State() internalChecked: boolean;

  @AttachInternals() internals: ElementInternals;

  /** Événement déclenché lors d'un changement d'état de la case à cocher (cochée ou décochée) */
  @Event({ bubbles: true, composed: true }) spwChange: EventEmitter<boolean>;

  private handleCheckbox() {
    if (!this.disabled) {
      this.internalChecked = !this.internalChecked;
      this.checked = this.internalChecked;
      this.updateFormValue();
      this.spwChange.emit(this.internalChecked);
    }
  }

  /** Méthode publique pour changer l'état de la case à cocher. */
  @Method()
  async toggleCheckedState(newVal: boolean) {
    this.internalChecked = newVal;
    this.checked = newVal;
    this.updateFormValue();
  }

  @Watch('checked')
  checkedChanged(newChecked: boolean) {
    this.internalChecked = newChecked;
    this.inputElement.checked = newChecked;
    this.updateFormValue();
  }

  private updateFormValue() {
    if (this.internalChecked) {
      this.internals.setFormValue('on');
    } else {
      this.internals.setFormValue(null);
    }
  }

  private handleKeydown(event) {
    if (event.key === ' ' || event.key === 'Enter') {
      // Space or Enter keys
      this.handleCheckbox();
      event.preventDefault();
    }
  }

  componentWillLoad() {
    this.internalChecked = this.checked;
    this.updateFormValue();
  }

  componentDidLoad() {
    this.el.closest('form')?.addEventListener('formdata', (e: FormDataEvent) => {
      if (this.internalChecked) {
        e.formData.set(this.name, 'on');
      }
    });
  }

  formResetCallback() {
    this.internalChecked = false;
    this.checked = false;
    this.updateFormValue();
  }

  private get elementClass(): { [className: string]: boolean } {
    return {
      'spw-checkbox__wrapper': true,
      'spw-checkbox__wrapper--disabled': this.disabled,
      'spw-checkbox__wrapper--checked': this.internalChecked,
      'spw-checkbox__wrapper--error': this.error,
    };
  }

  render() {
    const slot = this.el.innerHTML;
    const hasSlot = Boolean(slot);

    return (
      <div class="spw-checkbox">
        <input
          type="checkbox"
          ref={el => (this.inputElement = el)}
          checked={this.internalChecked}
          onChange={this.handleCheckbox.bind(this)}
          name={this.name}
          disabled={this.disabled}
          style={{ display: 'none' }}
        />
        {hasSlot && (
          <label htmlFor={this.name} class={`spw-checkbox__label ${this.disabled ? 'spw-checkbox__label--disabled' : ''}`} onClick={this.handleCheckbox.bind(this)}>
            <slot />
          </label>
        )}
        <div
          tabindex={this.disabled ? -1 : 0}
          onClick={this.handleCheckbox.bind(this)}
          onKeyDown={this.handleKeydown.bind(this)}
          role="checkbox"
          aria-checked={this.internalChecked ? 'true' : 'false'}
          aria-disabled={this.disabled ? 'true' : 'false'}
          aria-labelledby="label"
          class={this.elementClass}
        >
          {this.internalChecked && <spw-icon icon={this.icon}></spw-icon>}
        </div>
      </div>
    );
  }
}
